import ExcelJS from 'exceljs';
import { classificationService } from '../services/classificationService';
import { productService } from '../services/productService';
import { getUnits } from '../services/unitsService';
import {
  cellText,
  parseActiveCell,
  summarizeImportResult,
} from './classificationExcel';

export { summarizeImportResult };

export const PRODUCT_IMPORT_COL = {
  categoryRef: 'Categoría (código o nombre)',
  subRef: 'Subcategoría (código o nombre)',
  familyRef: 'Familia (código o nombre)',
  name: 'Nombre',
  unit: 'Unidad de medida',
  minStock: 'Stock mínimo',
  active: 'Activo',
  isInsumo: 'Tratar como insumo (Sí/No)',
  machineryReading: 'Registrar lectura vehículo/máquina (Sí/No)',
};

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row, labels) {
  labels.forEach((label, i) => {
    const c = row.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = HEADER_FILL;
  });
}

export async function downloadProductsImportTemplate(opts = {}) {
  const insumoEnabled = Boolean(opts.insumoEnabled);
  const machineryEnabled = Boolean(opts.machineryEnabled);
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Productos', { views: [{ state: 'frozen', ySplit: 2 }] });
  const headers = [
    PRODUCT_IMPORT_COL.categoryRef,
    PRODUCT_IMPORT_COL.subRef,
    PRODUCT_IMPORT_COL.familyRef,
    PRODUCT_IMPORT_COL.name,
    PRODUCT_IMPORT_COL.unit,
    PRODUCT_IMPORT_COL.minStock,
    PRODUCT_IMPORT_COL.active,
  ];
  if (insumoEnabled) headers.push(PRODUCT_IMPORT_COL.isInsumo);
  if (machineryEnabled) headers.push(PRODUCT_IMPORT_COL.machineryReading);

  const lastCol = String.fromCharCode('A'.charCodeAt(0) + headers.length - 1);
  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value =
    'Importación simple (sin atributos ni precios). El SKU lo genera el sistema. Indique categoría, subcategoría y familia como en el maestro de clasificación (código o nombre). Unidad de medida: debe existir en el maestro de unidades (código, símbolo o nombre; ej. UNI, KG, LT). Fila 2 = encabezados. Datos desde fila 3. Stock mínimo numérico (opcional, por defecto 0). Activo: Sí o No. Si hay módulos extra activos, complete sus columnas también en formato Sí o No.';
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = 64;
  styleHeaderRow(ws.getRow(2), headers);
  const columns = [
    { width: 28 },
    { width: 28 },
    { width: 24 },
    { width: 36 },
    { width: 14 },
    { width: 12 },
    { width: 10 },
  ];
  if (insumoEnabled) columns.push({ width: 20 });
  if (machineryEnabled) columns.push({ width: 30 });
  ws.columns = columns;
  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(buf, 'plantilla_importar_productos.xlsx');
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

function findHeaderRow(sheet, requiredLabels) {
  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const row = sheet.getRow(r);
    const map = headerMapFromRow(row);
    if (requiredLabels.every((label) => map[label] != null)) {
      return { rowIndex: r, colMap: map };
    }
  }
  return null;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

function formatApiError(err) {
  const d = err.response?.data;
  if (!d) return err.message || 'Error desconocido';
  if (d.message) return d.message;
  if (d.errors) {
    return Object.values(d.errors).flat().join('; ');
  }
  return 'Error al guardar';
}

function findCategoryByRef(categories, ref) {
  const q = String(ref).trim();
  if (!q) return null;
  const lower = q.toLowerCase();
  let c = categories.find((x) => String(x.code).toLowerCase() === lower);
  if (c) return c;
  c = categories.find((x) => String(x.name).trim().toLowerCase() === lower);
  return c || null;
}

function findSubcategoryByRef(subcategories, categoryId, ref) {
  const q = String(ref).trim();
  if (!q) return null;
  const lower = q.toLowerCase();
  const pool = subcategories.filter((s) => Number(s.category_id) === Number(categoryId));
  let s = pool.find((x) => String(x.code).toLowerCase() === lower);
  if (s) return s;
  s = pool.find((x) => String(x.name).trim().toLowerCase() === lower);
  return s || null;
}

function findFamilyByRef(families, subcategoryId, ref) {
  const q = String(ref).trim();
  if (!q) return null;
  const lower = q.toLowerCase();
  const pool = families.filter((f) => Number(f.subcategory_id) === Number(subcategoryId));
  let x = pool.find((f) => String(f.code).toLowerCase() === lower);
  if (x) return x;
  x = pool.find((f) => String(f.name).trim().toLowerCase() === lower);
  return x || null;
}

/** Resuelve unidad activa por código, símbolo o nombre (mismo criterio que el backend). */
function findUnitByRef(units, ref) {
  const q = String(ref).trim();
  if (!q) return null;
  const lower = q.toLowerCase();
  const upper = q.toUpperCase();

  let aliases = [lower, upper];
  if (lower === 'unidad' || lower === 'u' || lower === 'un') {
    aliases = [...aliases, 'un', 'UN', 'uni', 'UNI'];
  }

  const byCode = units.find((u) => {
    const code = String(u.code || '').trim();
    return aliases.some((a) => code.toLowerCase() === String(a).toLowerCase());
  });
  if (byCode) return byCode;

  const bySymbol = units.find((u) => {
    const symbol = String(u.symbol || '').trim();
    if (!symbol) return false;
    return symbol.toLowerCase() === lower || symbol.toUpperCase() === upper;
  });
  if (bySymbol) return bySymbol;

  const byName = units.find((u) => String(u.name || '').trim().toLowerCase() === lower);
  return byName || null;
}

function parseMinStock(raw) {
  if (raw == null || raw === '') return { ok: true, value: 0 };
  const n = parseInt(String(raw).trim(), 10);
  if (Number.isNaN(n) || n < 0) return { ok: false, value: 0 };
  return { ok: true, value: n };
}

async function loadFirstSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets[0] || null;
}

const REQUIRED = [
  PRODUCT_IMPORT_COL.categoryRef,
  PRODUCT_IMPORT_COL.subRef,
  PRODUCT_IMPORT_COL.familyRef,
  PRODUCT_IMPORT_COL.name,
  PRODUCT_IMPORT_COL.unit,
];

export async function analyzeProductsImport(file, opts = {}) {
  const insumoEnabled = Boolean(opts.insumoEnabled);
  const machineryEnabled = Boolean(opts.machineryEnabled);
  const sheet = await loadFirstSheet(file);
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  const found = findHeaderRow(sheet, REQUIRED);
  if (!found) {
    return {
      success: false,
      fatalMessage: `Faltan encabezados obligatorios. Debe existir: ${REQUIRED.join(', ')}.`,
    };
  }

  let categories;
  let subcategories;
  let families;
  let units;
  try {
    const [catRes, subRes, famRes, unitsRaw] = await Promise.all([
      classificationService.getCategories({ active_only: false }),
      classificationService.getSubcategories({ active_only: false }),
      classificationService.getFamilies({ active_only: false }),
      getUnits({ active_only: true }),
    ]);

    if (!catRes.success || !catRes.data) {
      return { success: false, fatalMessage: 'No se pudieron cargar las categorías.' };
    }
    if (!subRes.success || !subRes.data) {
      return { success: false, fatalMessage: 'No se pudieron cargar las subcategorías.' };
    }
    if (!famRes.success || !famRes.data) {
      return { success: false, fatalMessage: 'No se pudieron cargar las familias.' };
    }

    categories = catRes.data;
    subcategories = subRes.data;
    families = famRes.data;
    units = Array.isArray(unitsRaw?.data)
      ? unitsRaw.data
      : Array.isArray(unitsRaw)
        ? unitsRaw
        : [];
  } catch (e) {
    return { success: false, fatalMessage: 'No se pudieron cargar categorías o unidades de medida.' };
  }

  if (!units.length) {
    return {
      success: false,
      fatalMessage: 'No hay unidades de medida activas. Configure unidades antes de importar.',
    };
  }

  const { rowIndex: headerRow, colMap } = found;
  const last = getLastUsedRow(sheet);
  const previewRows = [];
  const validTasks = [];

  for (let r = headerRow + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const catRef = cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.categoryRef]));
    const subRef = cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.subRef]));
    const famRef = cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.familyRef]));
    const name = cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.name]));
    const unitRaw = cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.unit]));
    const minRaw = colMap[PRODUCT_IMPORT_COL.minStock]
      ? cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.minStock]))
      : '';
    const activeRaw = colMap[PRODUCT_IMPORT_COL.active]
      ? cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.active]))
      : '';
    const insumoRaw = colMap[PRODUCT_IMPORT_COL.isInsumo]
      ? cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.isInsumo]))
      : '';
    const machineryRaw = colMap[PRODUCT_IMPORT_COL.machineryReading]
      ? cellText(row.getCell(colMap[PRODUCT_IMPORT_COL.machineryReading]))
      : '';

    if (!catRef && !subRef && !famRef && !name && !unitRaw) {
      previewRows.push({
        excelRow: r,
        status: 'empty',
        cols: {},
        issue: '',
      });
      continue;
    }

    const colsPreview = {
      categoria: catRef,
      subcategoria: subRef,
      familia: famRef,
      nombre: name,
      unidad: unitRaw,
      stock: minRaw,
      activo: activeRaw || 'Sí',
      insumo: insumoRaw || 'No',
      lecturaMaquinaria: machineryRaw || 'No',
    };

    if (!catRef || !subRef || !famRef || !name || !unitRaw) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: 'Complete categoría, subcategoría, familia, nombre y unidad de medida.',
      });
      continue;
    }

    const category = findCategoryByRef(categories, catRef);
    if (!category) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: `Categoría no encontrada: "${catRef}".`,
      });
      continue;
    }

    const subcategory = findSubcategoryByRef(subcategories, category.id, subRef);
    if (!subcategory) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: `Subcategoría no encontrada en esa categoría: "${subRef}".`,
      });
      continue;
    }

    const family = findFamilyByRef(families, subcategory.id, famRef);
    if (!family) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: `Familia no encontrada en esa subcategoría: "${famRef}".`,
      });
      continue;
    }

    const unitMatch = findUnitByRef(units, unitRaw);
    if (!unitMatch) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: `Unidad de medida no válida: "${unitRaw}". Use código, símbolo o nombre del maestro de unidades.`,
      });
      continue;
    }

    const minParsed = parseMinStock(minRaw);
    if (!minParsed.ok) {
      previewRows.push({
        excelRow: r,
        status: 'invalid',
        cols: colsPreview,
        issue: 'Stock mínimo debe ser un número entero ≥ 0.',
      });
      continue;
    }

    const unitCode = String(unitMatch.code).trim().slice(0, 10);
    const is_active = colMap[PRODUCT_IMPORT_COL.active]
      ? parseActiveCell(activeRaw)
      : true;
    const is_insumo = insumoEnabled
      ? (colMap[PRODUCT_IMPORT_COL.isInsumo] ? parseActiveCell(insumoRaw) : false)
      : false;
    const machinery_register_enabled = machineryEnabled
      ? (colMap[PRODUCT_IMPORT_COL.machineryReading] ? parseActiveCell(machineryRaw) : false)
      : false;

    previewRows.push({
      excelRow: r,
      status: 'valid',
      cols: {
        ...colsPreview,
        categoria: `${category.code} — ${category.name}`,
        subcategoria: `${subcategory.code} — ${subcategory.name}`,
        familia: `${family.code} — ${family.name}`,
        unidad: `${unitCode}${unitMatch.name ? ` — ${unitMatch.name}` : ''}`,
        stock: String(minParsed.value),
        activo: is_active ? 'Sí' : 'No',
        insumo: is_insumo ? 'Sí' : 'No',
        lecturaMaquinaria: machinery_register_enabled ? 'Sí' : 'No',
      },
      issue: '',
    });

    validTasks.push({
      excelRow: r,
      payload: {
        family_id: family.id,
        name: name.slice(0, 200),
        unit_of_measure: unitCode,
        min_stock: minParsed.value,
        is_active,
        has_price_list: false,
        is_taxable: false,
        is_insumo,
        machinery_register_enabled,
      },
    });
  }

  return {
    success: true,
    previewRows,
    validTasks,
    validCount: validTasks.length,
    emptyCount: previewRows.filter((x) => x.status === 'empty').length,
    invalidCount: previewRows.filter((x) => x.status === 'invalid').length,
    showInsumoColumn: insumoEnabled || Boolean(colMap[PRODUCT_IMPORT_COL.isInsumo]),
    showMachineryColumn: machineryEnabled || Boolean(colMap[PRODUCT_IMPORT_COL.machineryReading]),
  };
}

export async function executeProductsImport(validTasks) {
  const result = { created: 0, skipped: 0, errors: [] };
  for (const { excelRow, payload } of validTasks) {
    try {
      const res = await productService.createProduct(payload);
      if (res.success) result.created += 1;
      else {
        const msg =
          res.errors && typeof res.errors === 'object'
            ? Object.values(res.errors).flat().join('; ')
            : res.message || 'Respuesta sin éxito';
        result.errors.push({ row: excelRow, message: msg });
      }
    } catch (e) {
      result.errors.push({ row: excelRow, message: formatApiError(e) });
    }
  }
  return result;
}
